/**
 * Amplify Configuration - EduQ AI Admin Portal
 * 
 * Configures AWS Amplify with admin-specific app client
 */

import { Amplify } from 'aws-amplify';

/**
 * Load and configure Amplify with admin app client
 * This function should be called once at app initialization
 */
export const configureAmplify = async () => {
  try {
    // Load amplify_outputs.json from public directory
    const response = await fetch('/amplify_outputs.json');
    
    if (!response.ok) {
      throw new Error(`Failed to load Amplify configuration: ${response.statusText}`);
    }
    
    const amplifyConfig = await response.json();
    
    // Validate required configuration
    if (!amplifyConfig.auth?.user_pool_id) {
      throw new Error('Missing user_pool_id in Amplify configuration');
    }
    
    if (!amplifyConfig.auth?.user_pool_client_id) {
      throw new Error('Missing user_pool_client_id in Amplify configuration');
    }
    
    // Check if admin app client is configured
    if (amplifyConfig.auth.user_pool_client_id === 'ADMIN_APP_CLIENT_ID_TO_BE_CREATED') {
      console.warn(
        '[Amplify Config] Admin app client ID not configured. ' +
        'Please create a dedicated app client in Cognito console and update amplify_outputs.json'
      );
    }
    
    // Configure Amplify with OAuth support for admin portal
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: amplifyConfig.auth.user_pool_id,
          userPoolClientId: amplifyConfig.auth.user_pool_client_id,
          identityPoolId: amplifyConfig.auth.identity_pool_id,
          loginWith: {
            email: true,
            oauth: amplifyConfig.auth.oauth ? {
              identityProviders: amplifyConfig.auth.oauth.identity_providers,
              domain: amplifyConfig.auth.oauth.domain,
              scopes: amplifyConfig.auth.oauth.scopes,
              redirectSignIn: amplifyConfig.auth.oauth.redirect_sign_in_uri,
              redirectSignOut: amplifyConfig.auth.oauth.redirect_sign_out_uri,
              responseType: amplifyConfig.auth.oauth.response_type,
            } : undefined,
          },
          signUpVerificationMethod: 'code',
          userAttributes: {
            email: {
              required: true,
            },
          },
          allowGuestAccess: false,
          passwordFormat: {
            minLength: amplifyConfig.auth.password_policy?.min_length || 8,
            requireLowercase: amplifyConfig.auth.password_policy?.require_lowercase ?? true,
            requireUppercase: amplifyConfig.auth.password_policy?.require_uppercase ?? true,
            requireNumbers: amplifyConfig.auth.password_policy?.require_numbers ?? true,
            requireSpecialCharacters: amplifyConfig.auth.password_policy?.require_symbols ?? true,
          },
        },
      },
    });
    
    console.log('[Amplify Config] Admin portal configured with OAuth support');
    
    console.log('[Amplify Config] Successfully configured with user pool:', amplifyConfig.auth.user_pool_id);
    
    return {
      success: true,
      userPoolId: amplifyConfig.auth.user_pool_id,
      region: amplifyConfig.auth.aws_region,
    };
  } catch (error) {
    console.error('[Amplify Config] Failed to configure Amplify:', error);
    throw error;
  }
};

/**
 * Get current Amplify configuration
 * @returns {Object} Current Amplify configuration
 */
export const getAmplifyConfig = () => {
  try {
    return Amplify.getConfig();
  } catch (error) {
    console.error('[Amplify Config] Failed to get configuration:', error);
    return null;
  }
};

/**
 * Validate Amplify configuration
 * @returns {boolean} True if configuration is valid
 */
export const validateAmplifyConfig = () => {
  try {
    const config = Amplify.getConfig();
    
    if (!config?.Auth?.Cognito?.userPoolId) {
      console.error('[Amplify Config] Missing user pool ID');
      return false;
    }
    
    if (!config?.Auth?.Cognito?.userPoolClientId) {
      console.error('[Amplify Config] Missing user pool client ID');
      return false;
    }
    
    // Check if using production user pool
    const expectedUserPoolId = 'ap-southeast-1_ISUlRZfpp';
    if (config.Auth.Cognito.userPoolId !== expectedUserPoolId) {
      console.warn(
        `[Amplify Config] User pool ID mismatch. Expected: ${expectedUserPoolId}, Got: ${config.Auth.Cognito.userPoolId}`
      );
    }
    
    return true;
  } catch (error) {
    console.error('[Amplify Config] Validation failed:', error);
    return false;
  }
};

export default {
  configureAmplify,
  getAmplifyConfig,
  validateAmplifyConfig,
};
